import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Field, FastField, useFormikContext, FieldArray } from "formik";
import DatePicker from "react-datepicker";
import moment from "moment";
import CreatableSelect from "react-select/creatable";
import AsyncSelect from "react-select/async";
import Select from "react-select";
import closeIcon from "../assets/icons/close.svg";
import infoIcon from "../assets/icons/info.svg";
import { ICE, GREY_MEDIUM_DARK } from "../constants/cssVars";
import { Tooltip } from "./general/Tooltip";
import {
  getOptionFromValue,
  getOptionsFromList,
} from "../constants/formOptions";
import { LinkLikeButton } from "./Button";
import {
  ErrorText,
  LabelledInput,
  InputWrapper,
  DeleteIcon,
  InfoIcon,
  FileUploadWrapper,
  RadioButtonWrapper,
  radioInputStyle,
  CheckboxWrapper,
  CheckboxGroupWrapper,
  MultiDatePickerWrapper,
  DeleteBtn,
} from "./formFieldStyles";

export { MultiFieldLine } from "./formFieldStyles";

export const DeleteButton = ({ onDelete }) => (
  <DeleteIcon onClick={onDelete} src={closeIcon} />
);

// Wrapper for field inputs, displaying labels and helper text
export const FieldWrapper = ({
  label,
  field,
  children,
  style,
  disabled,
  inputStyle,
  short,
  error,
  onDelete,
  hideLabel,
  id,
  info,
}) => {
  const { required } = field || {};
  return (
    <LabelledInput
      style={style}
      short={short}
      disabled={disabled}
      error={error}
    >
      {!hideLabel && (
        <label htmlFor={id}>
          {label}
          {field && required ? "*" : ""}
        </label>
      )}
      {info && (
        <Tooltip content={info}>
          <InfoIcon src={infoIcon} alt="more information" />
        </Tooltip>
      )}
      <InputWrapper style={inputStyle}>
        {children}
        {onDelete && <DeleteButton onDelete={onDelete} />}
      </InputWrapper>
      {error && (
        <label id={`errorMsg_${id}`} style={{ fontStyle: "italic" }}>
          {error}
        </label>
      )}
    </LabelledInput>
  );
};

export const TextField = ({ name, placeholder, disabled, ...props }) => (
  <FieldWrapper {...props} disabled={disabled} id={name}>
    <FastField
      id={name}
      onKeyUp={props.onKeyUp}
      disabled={disabled}
      name={name}
      placeholder={placeholder}
      aria-describedby={`errorMsg_${name}`}
      type={props.type}
      aria-invalid={props.error ? "true" : "false"}
    />
  </FieldWrapper>
);

export const SliderField = ({ name, placeholder, ...props }) => (
  <FieldWrapper {...props} id={name}>
    <Field
      className="slider"
      id={name}
      name={name}
      placeholder={placeholder}
      aria-describedby={`errorMsg_${name}`}
      type="range"
      aria-invalid={props.error ? "true" : "false"}
      max={props.max}
      min={props.min}
      step={props.step}
      list={props.list}
    />
  </FieldWrapper>
);

export const FileUploadField = (props) => {
  return (
    <FieldWrapper {...props} id={props.name}>
      <FileUploadWrapper>
        <input type="file" onChange={props.onFileChange} />
        {props.children}
      </FileUploadWrapper>
    </FieldWrapper>
  );
};

export const ParagraphTextField = ({ name, placeholder, ...props }) => (
  <FieldWrapper {...props} id={name}>
    <Field
      id={name}
      as="textarea"
      name={name}
      placeholder={placeholder}
      aria-describedby={`errorMsg_${name}`}
      aria-invalid={props.error ? "true" : "false"}
    />
  </FieldWrapper>
);

export const SelectField = (props) => {
  const { setFieldValue, values } = useFormikContext();
  const handleChange = (newValue) => {
    props.onChange && props.onChange(newValue.value);
    setFieldValue(props.name, newValue.value);
    // if (props.name === 'softwareName' && softwareInfoByDistributor[newValue.value]) {
    //   setFieldValue('softwareDistributionURL', softwareInfoByDistributor[newValue.value].distributionUrl);
    //   setFieldValue('softwareDistributor', softwareInfoByDistributor[newValue.value].distributor);
    // }
  };

  const optionSelector = props.optionSelector || getOptionFromValue;

  let currentValue = values[props.name];
  if (props.name.includes("contacts.")) {
    const contactNameArray = props.name.split(".");
    const contactIndex = contactNameArray[1];
    currentValue = values.contacts[contactIndex][contactNameArray[2]];
  }

  const selectedOption = optionSelector(currentValue);

  useEffect(() => {
    if (selectedOption != null) {
      if (selectedOption.value !== values[props.name]) {
        setFieldValue(props.name, selectedOption.value);
      }
    }
  }, [
    selectedOption,
    currentValue,
    optionSelector,
    props.name,
    setFieldValue,
    values,
  ]);

  return (
    <FieldWrapper {...props}>
      <Select
        defaultValue={optionSelector(currentValue)}
        className="custom-react-select"
        classNamePrefix="select"
        value={optionSelector(currentValue)}
        onChange={handleChange}
        options={props.options}
      />
    </FieldWrapper>
  );
};

SelectField.propTypes = {
  name: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.any,
      label: PropTypes.any,
    }),
  ),
  optionSelector: PropTypes.func,
  onChange: PropTypes.func,
};

export const MultiSelectField = (props) => {
  const { setFieldValue, values } = useFormikContext();
  const handleChange = (newValue) => {
    const listOfValues = newValue.map((obj) => obj.value);
    setFieldValue(props.name, listOfValues);
  };

  const optionSelector = props.optionSelector || getOptionsFromList;

  return (
    <FieldWrapper {...props}>
      <Select
        isMulti
        defaultValue={optionSelector(values[props.name])}
        value={optionSelector(values[props.name])}
        className="custom-react-select"
        classNamePrefix="select"
        onChange={handleChange}
        options={props.options}
      />
    </FieldWrapper>
  );
};

export const DatepickerField = (props) => {
  const { setFieldValue, values } = useFormikContext();
  const handleChange = (newValue) => {
    if (!newValue) {
      // Clear the date when picker is cleared
      setFieldValue(props.name, "");
      return;
    }
    setFieldValue(props.name, moment(newValue).format("YYYY-MM-DD"));
  };

  const rawValue = values[props.name];
  let dateValue = null;

  if (rawValue) {
    if (rawValue instanceof Date && !isNaN(rawValue)) {
      dateValue = rawValue;
    } else {
      // Accept ISO datetime (e.g., 2025-12-17T10:15:30) or date-only
      const parsedIso = moment(rawValue, moment.ISO_8601, true);
      const parsedDateOnly = moment(rawValue, "YYYY-MM-DD", true);
      const parsed = parsedIso.isValid() ? parsedIso : parsedDateOnly;
      if (parsed.isValid()) dateValue = parsed.toDate();
    }
  }

  return (
    <FieldWrapper {...props}>
      <DatePicker selected={dateValue} onChange={handleChange} />
    </FieldWrapper>
  );
};

// AsyncSelectField for server-side search (e.g., baseline model search)
export const AsyncSelectField = (props) => {
  const { setFieldValue, values } = useFormikContext();
  const { loadOptions, optionSelector, ...restProps } = props;

  const handleChange = (newValue) => {
    // Pass the full option object to onChange if provided, otherwise just the value
    if (props.onChange) {
      props.onChange(newValue);
    }
    setFieldValue(props.name, newValue ? newValue.value : null);
  };

  const currentValue = values[props.name];
  const currentOption =
    currentValue && optionSelector ? optionSelector(currentValue) : null;

  // Custom option renderer to show title with HUC and County as secondary info
  const formatOptionLabel = ({ label, item }) => {
    if (!item) return label;

    const parts = [];
    if (item.huc) parts.push(`HUC: ${item.huc}`);
    if (item.county) parts.push(`County: ${item.county}`);

    return (
      <div>
        <div style={{ fontWeight: 600 }}>{label}</div>
        {parts.length > 0 && (
          <div style={{ fontSize: "0.85em", color: "#666", marginTop: "2px" }}>
            {parts.join(" | ")}
          </div>
        )}
      </div>
    );
  };

  // Use key to force re-render when value changes
  const selectKey = currentOption
    ? `${props.name}_${currentOption.value?.tdisIdentifier || currentOption.value?.title || currentOption.label || "none"}`
    : `${props.name}_empty`;

  // Custom placeholder component if customPlaceholder prop is provided
  const customComponents = props.customPlaceholder
    ? {
        Placeholder: (placeholderProps) => {
          const { innerProps, getStyles } = placeholderProps;
          return (
            <div
              {...innerProps}
              style={getStyles("placeholder", placeholderProps)}
            >
              {props.customPlaceholder}
            </div>
          );
        },
      }
    : {};

  return (
    <FieldWrapper {...props}>
      <AsyncSelect
        key={selectKey}
        value={currentOption}
        onChange={handleChange}
        loadOptions={loadOptions}
        defaultOptions={false}
        cacheOptions={true}
        isClearable
        placeholder={props.placeholder || "Type to search..."}
        noOptionsMessage={({ inputValue }) =>
          inputValue && inputValue.length < 4
            ? "Type at least 4 characters to search"
            : "No results found"
        }
        formatOptionLabel={formatOptionLabel}
        className="custom-react-select"
        classNamePrefix="select"
        components={
          Object.keys(customComponents).length > 0
            ? customComponents
            : undefined
        }
        {...restProps}
      />
    </FieldWrapper>
  );
};

// AsyncMultiSelectField for server-side search with multi-select (e.g., baseline model multi-select)
export const AsyncMultiSelectField = (props) => {
  const { setFieldValue, values } = useFormikContext();
  const { loadOptions, optionSelector, ...restProps } = props;

  const handleChange = (selectedOptions) => {
    // selectedOptions is an array of option objects
    const valuesArray = selectedOptions
      ? selectedOptions.map((opt) => opt.value)
      : [];

    // Pass the full option objects to onChange if provided
    if (props.onChange) {
      props.onChange(selectedOptions);
    }
    setFieldValue(props.name, valuesArray);
  };

  const currentValue = values[props.name] || [];
  const currentOptions =
    Array.isArray(currentValue) && optionSelector
      ? currentValue.map((val) => optionSelector(val)).filter(Boolean)
      : [];

  // Custom option renderer to show title with HUC and County as secondary info
  const formatOptionLabel = ({ label, item }) => {
    if (!item) return label;

    const parts = [];
    if (item.huc) parts.push(`HUC: ${item.huc}`);
    if (item.county) parts.push(`County: ${item.county}`);

    return (
      <div>
        <div style={{ fontWeight: 600 }}>{label}</div>
        {parts.length > 0 && (
          <div style={{ fontSize: "0.85em", color: "#666", marginTop: "2px" }}>
            {parts.join(" | ")}
          </div>
        )}
      </div>
    );
  };

  // Custom Option component to style selected (disabled) options with a more pleasing color
  const Option = (optionProps) => {
    const { innerRef, innerProps } = optionProps;
    const isDisabled = optionProps.isDisabled || false;

    // Get default styles
    const defaultStyles = optionProps.getStyles("option", optionProps);

    // Custom styling for disabled (already selected) options
    const customStyles = isDisabled
      ? {
          ...defaultStyles,
          backgroundColor: ICE, // Light grey background from design system
          color: GREY_MEDIUM_DARK, // Muted grey text
          cursor: "not-allowed",
          opacity: 0.8,
        }
      : {
          ...defaultStyles,
          cursor: "pointer",
        };

    return (
      <div ref={innerRef} {...innerProps} style={customStyles}>
        {optionProps.children}
      </div>
    );
  };

  // Custom placeholder component if customPlaceholder prop is provided
  const customComponents = {
    // Hide X icons on multi-select items - users can use backspace to delete sequentially
    MultiValueRemove: () => null,
    // Custom Option component to style disabled options
    Option: Option,
    ...(props.customPlaceholder
      ? {
          Placeholder: (placeholderProps) => {
            const { innerProps, getStyles } = placeholderProps;
            return (
              <div
                {...innerProps}
                style={getStyles("placeholder", placeholderProps)}
              >
                {props.customPlaceholder}
              </div>
            );
          },
        }
      : {}),
  };

  // Custom function to get a unique identifier for each option
  // This helps react-select identify when the same model appears in different searches
  const getOptionValue = (option) => {
    if (!option || !option.value) return null;

    // For "External Model", use the string as identifier
    if (option.value === "External Model") {
      return "external-model";
    }

    // For real models, use tdisIdentifier as the unique key
    if (typeof option.value === "object" && option.value.tdisIdentifier) {
      return option.value.tdisIdentifier;
    }

    // Fallback to the value itself
    return option.value;
  };

  // Function to check if an option is already selected (disabled)
  // This is passed as a prop from MetadataForm
  const isOptionDisabled = props.isOptionDisabled || (() => false);

  return (
    <FieldWrapper {...props}>
      <AsyncSelect
        isMulti
        value={currentOptions}
        onChange={handleChange}
        loadOptions={loadOptions}
        defaultOptions={false}
        cacheOptions={true}
        isClearable
        hideSelectedOptions={false}
        getOptionValue={getOptionValue}
        isOptionDisabled={isOptionDisabled}
        placeholder={props.placeholder || "Type to search..."}
        noOptionsMessage={({ inputValue }) =>
          inputValue && inputValue.length < 4
            ? "Type at least 4 characters to search"
            : "No results found"
        }
        formatOptionLabel={formatOptionLabel}
        className="custom-react-select"
        classNamePrefix="select"
        components={customComponents}
        {...restProps}
      />
    </FieldWrapper>
  );
};

export const RadioButtonField = ({ name, options, ...props }) => (
  <FieldWrapper {...props} inputStyle={radioInputStyle}>
    {options.map((option) => (
      <RadioButtonWrapper key={option.value}>
        <Field
          id={name}
          style={{ width: "auto" }}
          type="radio"
          value={option.value}
          name={name}
          aria-describedby={`errorMsg_${name}`}
          aria-invalid={props.error ? "true" : "false"}
        />
        <div style={{ marginLeft: "5px" }}>{option.label}</div>
      </RadioButtonWrapper>
    ))}
  </FieldWrapper>
);

const Checkbox = ({
  name,
  value,
  error,
  testId,
  label,
  children,
  wrapperStyle,
}) => (
  <CheckboxWrapper wrapperStyle={wrapperStyle}>
    <Field
      id={name}
      data-testid={testId}
      className="regular-checkbox"
      type="checkbox"
      name={name}
      value={value}
      aria-describedby={`errorMsg_${name}`}
      aria-invalid={error ? "true" : "false"}
    />
    {children}
    {label}
  </CheckboxWrapper>
);

export const CheckboxField = ({ error, ...props }) => (
  <div>
    <Checkbox {...props} />
    {error && <ErrorText>{error}</ErrorText>}
  </div>
);

export const CheckboxGroup = ({
  checkboxOptions,
  name,
  error,
  labelId,
  label,
  style,
  checkboxStyle,
}) => (
  <LabelledInput error={error}>
    <label id={labelId}>{label}</label>
    <CheckboxGroupWrapper
      role="group"
      aria-labelledby={labelId}
      style={style}
      aria-describedby={`errorMsg_${name}`}
    >
      {checkboxOptions.map((props) => (
        <Checkbox
          {...props}
          name={name}
          wrapperStyle={checkboxStyle}
          key={props.value}
        />
      ))}
    </CheckboxGroupWrapper>
    {error && <ErrorText>{error}</ErrorText>}
  </LabelledInput>
);

export const OptionsList = ({ hideSelectOne, options, name }) => {
  const fullOptions = [
    ...(hideSelectOne ? [] : [{ value: "", label: "Select One" }]),
    ...options,
  ];
  return fullOptions.map((option) => (
    <option key={`${name}_${option.value}`} value={option.value}>
      {option.label}
    </option>
  ));
};

// If we need to make this on-outside-click create: https://github.com/JedWatson/react-select/issues/1764
export const CreatableMulti = (props) => {
  const { setFieldValue, values } = useFormikContext();

  const handleChange = (newValue) => {
    const listOfValues = newValue.map((obj) => obj.value);
    setFieldValue(props.name, listOfValues);
  };

  return (
    <FieldWrapper {...props} id={props.name}>
      <CreatableSelect
        defaultValue={getOptionsFromList(values[props.name])}
        className="custom-react-select"
        isMulti={true}
        onChange={handleChange}
        options={props.options}
      />
    </FieldWrapper>
  );
};

const components = {
  DropdownIndicator: null,
};

const createOption = (label) => ({
  label,
  value: label,
});

// Helper function to parse value array from Formik value
const parseValueArray = (formikValue) => {
  if (typeof formikValue === "string") {
    return formikValue
      .split(",")
      .map((value) => value.trim())
      .filter((v) => v);
  }
  if (Array.isArray(formikValue)) {
    return formikValue;
  }
  return [];
};

export const CreatableInputOnly = (props) => {
  const { setFieldValue, values } = useFormikContext();
  const [inputValue, setInputValue] = useState("");

  const valueArray = parseValueArray(values[props.name]);
  const [value, setValue] = useState(getOptionsFromList(valueArray));

  // Sync internal state when Formik value changes externally
  useEffect(() => {
    const newValueArray = parseValueArray(values[props.name]);
    const newValue = getOptionsFromList(newValueArray);
    // Only update if the value actually changed to avoid infinite loops
    const compareValues = (a, b) =>
      String(a).localeCompare(String(b), undefined, { numeric: true });
    const currentValues = value
      .map((obj) => obj.value)
      .sort(compareValues)
      .join(",");
    const newValues = newValue
      .map((obj) => obj.value)
      .sort(compareValues)
      .join(",");
    if (currentValues !== newValues) {
      setValue(newValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values[props.name], props.name]);

  useEffect(() => {
    const listOfValues = value.map((obj) => obj.value);
    setFieldValue(props.name, listOfValues);
  }, [value, props.name, setFieldValue]);

  const handleChange = (value) => {
    setValue(value);
  };

  const handleBlur = () => {
    const label = inputValue?.trim() || "";
    // const optionExists = options.find((opt) => opt.label === label);

    if (!label) {
      return;
    }

    const option = { label, value: label };

    // // Add new option to menu list
    // setOptions([...options, option]);
    // Add value to selected options
    setValue([...(value || []), option]);
    // Clear input value
    setInputValue("");
  };

  const handleInputChange = (textInput, { action }) => {
    if (action === "input-change") {
      setInputValue(textInput);
    }
    if (action === "input-blur") {
      handleBlur();
    }
  };

  const handleKeyDown = (event) => {
    if (!inputValue) return;
    switch (event.key) {
      case "Enter":
      case "Tab": {
        const newValues = [...value, createOption(inputValue)];
        setInputValue("");
        setValue(newValues);
        event.preventDefault();
        break;
      }
      default:
        break;
    }
  };

  return (
    <FieldWrapper {...props} id={props.name}>
      <CreatableSelect
        components={components}
        inputValue={inputValue}
        className="custom-react-select"
        isClearable
        isMulti
        menuIsOpen={false}
        onChange={handleChange}
        onInputChange={handleInputChange}
        onKeyDown={handleKeyDown}
        placeholder="Type and press enter..."
        value={value}
      />
    </FieldWrapper>
  );
};

CreatableInputOnly.propTypes = {
  name: PropTypes.string.isRequired,
};

export const DatepickerRangeField = (props) => {
  const { setFieldValue, values } = useFormikContext();
  const handleChange = (newValue, index) => {
    setFieldValue(`${props.name}.${index}`, newValue);
  };

  return (
    <FieldWrapper {...props}>
      <FieldArray
        name={props.name}
        render={(arrayHelpers) => (
          <div>
            {values[props.name].map((friend, index) => {
              const isArray =
                values[props.name] &&
                Array.isArray(values[props.name][index]) &&
                values[props.name][index].length > 0;
              // const valuesAreInitialized = Array.isArray(values[props.name]) && Array.isArray(values[props.name][index]) && values[props.name].length > 1;
              const startDate = isArray ? values[props.name][index][0] : null;
              const endDate = isArray ? values[props.name][index][1] : null;

              return (
                <MultiDatePickerWrapper key={index}>
                  <DatePicker
                    selected={startDate}
                    onChange={(update) => {
                      handleChange([update, endDate], index);
                    }}
                  />
                  <div style={{ margin: "0px 5px" }}>to</div>
                  <DatePicker
                    selected={endDate}
                    onChange={(update) => {
                      handleChange([startDate, update], index);
                    }}
                  />
                  <DeleteBtn
                    type="button"
                    onClick={() => arrayHelpers.remove(index)}
                  >
                    <DeleteIcon src={closeIcon} />
                  </DeleteBtn>
                </MultiDatePickerWrapper>
              );
            })}
            {/* <div style={{ fontSize: '0.9em', fontStyle: 'italic'}}>Temporarily disabling multiple time period inputs</div> */}
            <div style={{ textAlign: props.right ? "right" : "left" }}>
              <LinkLikeButton
                type="button"
                onClick={() => arrayHelpers.push([])}
              >
                + Add another
              </LinkLikeButton>
            </div>
          </div>
        )}
      />
    </FieldWrapper>
  );
};
